/**
 * Extract storable seat-map payload from API `results` (Sabre object or Amadeus array).
 * Returns [] when there are no cabins, or the error object when the API failed.
 */
export function resolveSeatMapResultsForCache(results) {
  if (!results) {
    return [];
  }
  if (results.error) {
    return results.error;
  }
  if (Array.isArray(results)) {
    const hasCabins = results.some(
      (item) => Array.isArray(item?.cabins) && item.cabins.length > 0
    );
    return hasCabins ? results : [];
  }
  if (Array.isArray(results.cabins) && results.cabins.length > 0) {
    return results;
  }
  return [];
}

export function cacheEntryHasSeatMapCabins(cached) {
  if (!cached) {
    return false;
  }
  if (cached.error || (cached.message && !cached.cabins && !Array.isArray(cached))) {
    return false;
  }
  if (Array.isArray(cached)) {
    return cached.some(
      (item) => Array.isArray(item?.cabins) && item.cabins.length > 0
    );
  }
  return Array.isArray(cached.cabins) && cached.cabins.length > 0;
}

/**
 * True when the cache entry is a terminal seat-map API failure
 * (e.g. `{ error: { message } }` or a bare `{ message }` error object).
 */
export function cacheEntryIsTerminalError(cached) {
  if (!cached || Array.isArray(cached)) {
    return false;
  }
  if (cached.error) {
    return true;
  }
  return Boolean(cached.message && !cached.cabins);
}

/**
 * True when the cache entry should stop further fetches for that flight —
 * either usable cabins or a terminal error.
 */
export function cacheEntryIsResolved(cached) {
  return cacheEntryHasSeatMapCabins(cached) || cacheEntryIsTerminalError(cached);
}

/**
 * Wrap a Redux cache entry for SeatMapWrapper props.
 * Terminal errors keep `results.error` intact (do not run cabin normalize).
 */
export function wrapSeatMapCacheEntryForProps(cached, flight = {}) {
  if (cacheEntryIsTerminalError(cached)) {
    const results = cached.error ? cached : { error: cached };
    return { results };
  }
  return normalizeSeatMapData({ results: cached }, flight.id, flight);
}

/**
 * Legacy singleSeatMap detail shape (Sabre object or first Amadeus segment).
 */
export function toSeatMapDetailPayload(cached) {
  if (!cached) {
    return [];
  }
  if (cached.error || (cached.message && !cached.cabins && !Array.isArray(cached))) {
    return cached;
  }
  if (Array.isArray(cached)) {
    return cached[0] || [];
  }
  return cached;
}

/**
 * Normalize seat map API response to @gigi/seat-map array format (Amadeus / Sabre).
 */
export function normalizeSeatMapData(apiResponse, currentFlightId, currentFlight = {}) {
  if (!apiResponse || !apiResponse.results) {
    return apiResponse;
  }

  if (Array.isArray(apiResponse.results)) {
    return apiResponse;
  }

  return {
    ...apiResponse,
    results: [
      {
        cabins: apiResponse.results.cabins || [],
        flights: {
          id: currentFlightId || currentFlight.id || '',
          number: currentFlight.number || '',
          operatingFlightNumber:
            currentFlight.operatingFlightNumber || currentFlight.number || '',
          carrier: currentFlight.carrier || '',
          departureAirport: currentFlight.departureAirport || '',
          departureAirportCity: currentFlight.departureAirportCity || '',
          arrivalAirport: currentFlight.arrivalAirport || '',
          arrivalAirportCity: currentFlight.arrivalAirportCity || '',
          operatingCarrier:
            apiResponse.results.operatingCarrier ||
            currentFlight.operatingCarrier ||
            currentFlight.carrier ||
            '',
          marketingCarrier:
            apiResponse.results.marketingCarrier ||
            currentFlight.marketingCarrier ||
            currentFlight.carrier ||
            '',
        },
      },
    ],
  };
}

/**
 * Booking page: flightBooking.bookingItenaries is the priced itineraryFare object
 * (set via setItenaryData), not a wrapper with .itineraryFare.
 */
export function isItineraryFareObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Boolean(
    value.paxFares ||
      value.platingCarrierDetails ||
      value.emdIssuanceAllowed !== undefined ||
      value.totalAmount ||
      value.baseAmount
  );
}

export function resolveBookingPageItineraryFare({
  bookingItinerariesFare,
  priceFareData,
  bookingFareId,
}) {
  const pricedFare = isItineraryFareObject(bookingItinerariesFare)
    ? bookingItinerariesFare
    : bookingItinerariesFare?.itineraryFare ||
      bookingItinerariesFare?.fareDetails?.itineraryFare ||
      priceFareData?.itineraryFare;

  return {
    ...(pricedFare || {}),
    id: bookingFareId,
  };
}

export function getFlightCacheKey(flight, fareId, from) {
  if (from === 'booking') {
    return `${flight.id}_${fareId}`;
  }
  return `${flight.id}-${flight.number}`;
}

export function getCachedSeatMapEntry(seatMapArray, key) {
  if (!seatMapArray || !seatMapArray.length) {
    return null;
  }
  const entry = seatMapArray.find((obj) => obj[key]);
  return entry ? entry[key] : null;
}

/**
 * Split Redux seatMap cache into SeatMapWrapper props (first flight + rest).
 * Includes terminal error entries so fetch gates stop and unavailable UI can render.
 * Emits a contiguous resolved prefix so multipleSeatMapData[i] maps to flights[i + 1].
 */
export function mapSeatMapCacheToWrapperProps(flights, seatMapArray, fareId, from) {
  const seatMapDataa = [];
  const multipleSeatMapData = [];

  if (!flights || flights.length === 0) {
    return { seatMapDataa, multipleSeatMapData };
  }

  for (let index = 0; index < flights.length; index++) {
    const flight = flights[index];
    const key = getFlightCacheKey(flight, fareId, from);
    let cached = getCachedSeatMapEntry(seatMapArray, key);
    if (!cached && flight?.id != null && flight?.number != null) {
      cached = getCachedSeatMapEntry(
        seatMapArray,
        `${flight.id}-${flight.number}`
      );
    }
    if (!cached || !cacheEntryIsResolved(cached)) {
      break;
    }
    const wrapped = wrapSeatMapCacheEntryForProps(cached, flight);
    if (index === 0) {
      seatMapDataa.push(wrapped);
    } else {
      multipleSeatMapData.push(wrapped);
    }
  }

  return { seatMapDataa, multipleSeatMapData };
}

export function mapPassengersForSeatMap(passengerData) {
  if (!passengerData || !passengerData.length) {
    return [];
  }
  return passengerData.map((p) => ({
    ...p,
    id: p.id != null ? String(p.id) : p.id,
    addOns: Array.isArray(p.addOns) ? p.addOns : [],
  }));
}

/**
 * Build a stable flight key for an addOn entry. Prefers `flightId`, falls back
 * to `flightNumber`. Returns `null` when neither is usable so the caller can
 * skip indexing entries that can't be matched safely.
 */
function getAddOnFlightKey(addOn) {
  if (!addOn) return null;
  if (addOn.flightId != null && addOn.flightId !== '') {
    return `id:${String(addOn.flightId)}`;
  }
  if (addOn.flightNumber != null && addOn.flightNumber !== '') {
    return `num:${String(addOn.flightNumber)}`;
  }
  return null;
}

/**
 * Look up the matching updated addOn for an original addOn, trying both
 * flight identifiers so we still pair entries when one side has `flightId`
 * and the other only has `flightNumber` (the booking-page scaffold only
 * carries `flightNumber`, whereas the seat-map can stamp `flightId` too).
 */
function findUpdatedAddOnForOrig(origAo, updatedByKey) {
  if (!origAo) return null;
  if (origAo.flightId != null && origAo.flightId !== '') {
    const hit = updatedByKey.get(`id:${String(origAo.flightId)}`);
    if (hit) return hit;
  }
  if (origAo.flightNumber != null && origAo.flightNumber !== '') {
    const hit = updatedByKey.get(`num:${String(origAo.flightNumber)}`);
    if (hit) return hit;
  }
  return null;
}

/**
 * Merge seat addOns emitted by the seat-map modal back into the parent's
 * original passenger array.
 *
 * Booking-page passengers are bootstrapped with one scaffold addOn per flight
 * carrying `meal / assistance / wheelChair / carrier / flightNumber` and no
 * `seat`. The seat-map mutates those scaffolds in place (spread-preserving the
 * non-seat keys) before emitting `updatedPassengers`. The merge therefore has
 * to align entries per-flight, not by "has seat vs. has no seat" — otherwise
 * the original scaffold and the seat-bearing copy from the modal both survive,
 * producing duplicate addOns that compound on every interaction.
 *
 * Strategy per passenger (matched on `passenger.id`, stringified):
 *   1. Walk `orig.addOns`; for each entry, find the matching updated entry by
 *      flight key (`flightId` first, `flightNumber` fallback) and replace its
 *      `seat` (use `updated.seat` when set, drop `seat` otherwise so unselect
 *      still propagates). All other keys on the original are kept verbatim.
 *   2. Append any updated entries whose flight key isn't already covered (so
 *      flights the modal saw but the page didn't pre-scaffold land exactly
 *      once).
 */
export function mergeSeatSelectionsIntoPassengers(
  originalPassengers,
  updatedPassengers
) {
  if (!Array.isArray(originalPassengers) || !originalPassengers.length) {
    return originalPassengers || [];
  }
  const byId = new Map();
  (updatedPassengers || []).forEach((p) => {
    if (p && p.id != null) {
      byId.set(String(p.id), p);
    }
  });
  return originalPassengers.map((orig) => {
    const updated = byId.get(String(orig?.id));
    if (!updated) {
      return orig;
    }

    const origAddOns = Array.isArray(orig.addOns) ? orig.addOns : [];
    const updatedAddOns = Array.isArray(updated.addOns) ? updated.addOns : [];

    const updatedByKey = new Map();
    updatedAddOns.forEach((ao) => {
      const key = getAddOnFlightKey(ao);
      if (key && !updatedByKey.has(key)) {
        updatedByKey.set(key, ao);
      }
    });

    const consumedUpdatedKeys = new Set();
    const mergedAddOns = origAddOns.map((origAo) => {
      const updatedAo = findUpdatedAddOnForOrig(origAo, updatedByKey);
      if (!updatedAo) {
        return origAo;
      }
      const updatedKey = getAddOnFlightKey(updatedAo);
      if (updatedKey) {
        consumedUpdatedKeys.add(updatedKey);
      }
      if (updatedAo.seat && updatedAo.seat.seatNumber) {
        return { ...origAo, seat: updatedAo.seat };
      }
      const { seat: _droppedSeat, ...origWithoutSeat } = origAo || {};
      return origWithoutSeat;
    });

    const extraAddOns = updatedAddOns.filter((ao) => {
      const key = getAddOnFlightKey(ao);
      if (!key) return false;
      if (consumedUpdatedKeys.has(key)) return false;
      consumedUpdatedKeys.add(key);
      return true;
    });

    return {
      ...orig,
      addOns: [...mergedAddOns, ...extraAddOns],
    };
  });
}

/**
 * Hydrate passengers' `addOns` from the canonical Redux seat[] array. This is
 * the inverse of `passengersToSeatRedux` and is required because parent data
 * sources (e.g. `bookingData.passengers`) don't always carry the latest seat
 * selections as addOns. Without this, reopening the seat-map modal after a
 * selection wouldn't show previously-checked seats — the wrapper relies on
 * `passenger.addOns[*].seat.seatNumber` to decide if a seat is selected.
 *
 * Authoritative semantics: `seatArr` is the single source of truth for seat
 * selections. For every per-flight entry already on a passenger, its `seat`
 * is replaced from `seatArr` (or dropped when `seatArr` has nothing for that
 * flight). Seats present in `seatArr` for flights not yet scaffolded on the
 * passenger are appended exactly once. Non-seat fields on existing addOn
 * entries (meal / assistance / wheelChair / carrier / code / ...) are
 * preserved verbatim. An empty `seatArr` therefore means "no seats selected"
 * (e.g. after the user removed everything), not "ignore me".
 *
 * Callers must only invoke this once the Redux seat[] reflects the current
 * intent (i.e. after a user interaction or the per-page bootstrap effect that
 * seeds Redux from bookingData passengers). Calling it before that bootstrap
 * would wipe seats sourced from bookingData.
 */
export function mergeSeatsIntoPassengers(passengers, seatArr, flights = []) {
  if (!Array.isArray(passengers) || passengers.length === 0) {
    return passengers || [];
  }

  const seatsByPassengerId = new Map();
  (seatArr || []).forEach((s) => {
    if (!s || s.passengerId == null || !s.seat) {
      return;
    }
    const key = String(s.passengerId);
    if (!seatsByPassengerId.has(key)) {
      seatsByPassengerId.set(key, []);
    }
    seatsByPassengerId.get(key).push(s);
  });

  const flightsArr = Array.isArray(flights) ? flights : [];

  const buildSeatObjFromSeatEntry = (s) => {
    const seatObj = { seatNumber: s.seat };
    if (s.amount != null && Number(s.amount) > 0) {
      seatObj.totalAmount = {
        amount: s.amount,
        currency: s.currency,
      };
    }
    if (s.aLaCarteOfferItemID) {
      seatObj.aLaCarteOfferItemID = s.aLaCarteOfferItemID;
    }
    return seatObj;
  };

  const getSeatEntryFlightKey = (s) => {
    if (s.id != null && s.id !== '') return `id:${String(s.id)}`;
    if (s.flightNumber != null && s.flightNumber !== '') {
      return `num:${String(s.flightNumber)}`;
    }
    return null;
  };

  const findSeatEntryForOrigAddOn = (origAo, seatsByKey) => {
    if (!origAo) return null;
    if (origAo.flightId != null && origAo.flightId !== '') {
      const hit = seatsByKey.get(`id:${String(origAo.flightId)}`);
      if (hit) return hit;
    }
    if (origAo.flightNumber != null && origAo.flightNumber !== '') {
      const hit = seatsByKey.get(`num:${String(origAo.flightNumber)}`);
      if (hit) return hit;
    }
    return null;
  };

  return passengers.map((p) => {
    const existingAddOns = Array.isArray(p?.addOns) ? p.addOns : [];
    const seatsForPax = seatsByPassengerId.get(String(p?.id)) || [];

    const seatsByKey = new Map();
    seatsForPax.forEach((s) => {
      const key = getSeatEntryFlightKey(s);
      if (key && !seatsByKey.has(key)) {
        seatsByKey.set(key, s);
      }
    });

    const consumedSeatKeys = new Set();
    const mergedAddOns = existingAddOns.map((origAo) => {
      const seatEntry = findSeatEntryForOrigAddOn(origAo, seatsByKey);
      if (!seatEntry) {
        if (!origAo?.seat?.seatNumber) {
          return origAo;
        }
        const { seat: _droppedSeat, ...origWithoutSeat } = origAo || {};
        return origWithoutSeat;
      }
      const seatKey = getSeatEntryFlightKey(seatEntry);
      if (seatKey) {
        consumedSeatKeys.add(seatKey);
      }
      return { ...origAo, seat: buildSeatObjFromSeatEntry(seatEntry) };
    });

    const extraAddOns = seatsForPax
      .filter((s) => {
        const key = getSeatEntryFlightKey(s);
        if (!key) return false;
        if (consumedSeatKeys.has(key)) return false;
        consumedSeatKeys.add(key);
        return true;
      })
      .map((s) => {
        const matchingFlight =
          flightsArr.find(
            (f) =>
              (f.id != null &&
                (f.id === s.id || String(f.id) === String(s.id))) ||
              (f.number != null &&
                (f.number === s.flightNumber ||
                  String(f.number) === String(s.flightNumber)))
          ) || {};

        return {
          flightNumber:
            s.flightNumber != null ? s.flightNumber : matchingFlight.number,
          flightId: s.id != null ? s.id : matchingFlight.id,
          carrier:
            matchingFlight.carrier || matchingFlight.operatingCarrier || '',
          seat: buildSeatObjFromSeatEntry(s),
        };
      });

    return {
      ...p,
      addOns: [...mergedAddOns, ...extraAddOns],
    };
  });
}

/**
 * Convert seat-map passenger addOns back to legacy Redux seat[] shape.
 *
 * `originalPassengers` (optional) preserves the source passenger-id type — the
 * wrapper internally stringifies ids, but downstream consumers
 * (BookingConfirmationContainer.updateSeat) compare with `===`, so a
 * stringified id breaks the match when the PNR uses numeric ids.
 */
export function passengersToSeatRedux(
  passengers,
  flights = [],
  originalPassengers = null
) {
  const seats = [];
  if (!passengers || !passengers.length) {
    return seats;
  }

  const idMap = new Map();
  if (Array.isArray(originalPassengers)) {
    originalPassengers.forEach((p) => {
      if (p && p.id != null) {
        idMap.set(String(p.id), p.id);
      }
    });
  }

  passengers.forEach((p) => {
    if (p.paxType === 'InfantInLap') {
      return;
    }
    (p.addOns || []).forEach((ao) => {
      const seatNumber = ao?.seat?.seatNumber;
      if (!seatNumber) {
        return;
      }
      const flight =
        flights.find(
          (f) =>
            f.id === ao.flightId ||
            f.id?.toString() === ao.flightId?.toString() ||
            f.number === ao.flightNumber ||
            f.number?.toString() === ao.flightNumber?.toString()
        ) || {};
      const originalId = idMap.get(String(p.id));
      seats.push({
        passengerId: originalId != null ? originalId : p.id,
        seat: seatNumber,
        flightNumber: ao.flightNumber || flight.number,
        id: ao.flightId || flight.id,
        amount: ao.seat?.totalAmount?.amount ?? 0,
        currency: ao.seat?.totalAmount?.currency,
        aLaCarteOfferItemID: ao.seat?.aLaCarteOfferItemID,
      });
    });
  });

  return seats;
}

function findFlightForSeatEntry(flights, seatEntry) {
  if (!Array.isArray(flights) || !seatEntry) {
    return null;
  }
  return (
    flights.find(
      (f) =>
        (seatEntry.id != null && String(f.id) === String(seatEntry.id)) ||
        (seatEntry.flightNumber != null &&
          String(f.number) === String(seatEntry.flightNumber))
    ) || null
  );
}

function findOriginalSeatAddOn(passenger, seatEntry) {
  const addOns = passenger?.addOns;
  if (!Array.isArray(addOns) || !seatEntry) {
    return null;
  }
  return (
    addOns.find((ao) => {
      if (
        seatEntry.id != null &&
        ao.flightId != null &&
        String(ao.flightId) === String(seatEntry.id)
      ) {
        return true;
      }
      if (
        seatEntry.flightNumber != null &&
        ao.flightNumber != null &&
        String(ao.flightNumber) === String(seatEntry.flightNumber)
      ) {
        return true;
      }
      return false;
    }) || null
  );
}

function buildPnrSeatFromSeatEntry(seatEntry) {
  const seatNumber = seatEntry?.seat;
  if (!seatNumber) {
    return null;
  }
  const amount = seatEntry.amount;
  if (amount === 0 || amount === undefined || amount === null) {
    return {
      seatNumber,
      ...(seatEntry.aLaCarteOfferItemID
        ? { aLaCarteOfferItemID: seatEntry.aLaCarteOfferItemID }
        : {}),
    };
  }
  return {
    seatNumber,
    totalAmount: {
      amount,
      currency: seatEntry.currency,
    },
    ...(seatEntry.aLaCarteOfferItemID
      ? { aLaCarteOfferItemID: seatEntry.aLaCarteOfferItemID }
      : {}),
  };
}

function upsertPassengerOperation(operations, passengerId, addOn) {
  const existing = operations.find(
    (op) => String(op.passengerId) === String(passengerId)
  );
  if (existing) {
    existing.addOns.push(addOn);
    return;
  }
  operations.push({
    passengerId,
    id: String(operations.length + 1),
    addOns: [addOn],
  });
}

/**
 * Build add/update PNR seat operations containing only passenger+flight
 * combinations that changed vs the original booking passengers.
 */
export function buildSeatPnrOperationsFromDelta(
  originalPassengers,
  currentSeatArr,
  flights = []
) {
  const addOperations = [];
  const updateOperations = [];

  if (!Array.isArray(currentSeatArr) || currentSeatArr.length === 0) {
    return { addOperations, updateOperations };
  }

  const passengers = Array.isArray(originalPassengers) ? originalPassengers : [];

  currentSeatArr.forEach((seatEntry) => {
    if (!seatEntry || seatEntry.passengerId == null || !seatEntry.seat) {
      return;
    }

    const passenger = passengers.find(
      (p) => String(p.id) === String(seatEntry.passengerId)
    );
    if (!passenger) {
      return;
    }

    const originalAddOn = findOriginalSeatAddOn(passenger, seatEntry);
    const originalSeatNumber = originalAddOn?.seat?.seatNumber;
    const hasOriginalSeat =
      originalSeatNumber != null && String(originalSeatNumber).trim() !== '';
    const newSeatNumber = String(seatEntry.seat);

    if (hasOriginalSeat && String(originalSeatNumber) === newSeatNumber) {
      return;
    }

    const seatPayload = buildPnrSeatFromSeatEntry(seatEntry);
    if (!seatPayload) {
      return;
    }

    const flight = findFlightForSeatEntry(flights, seatEntry);
    const addOn = {
      flightNumber: seatEntry.flightNumber || flight?.number,
      carrier: flight?.carrier || '',
      seat: seatPayload,
    };

    if (!hasOriginalSeat) {
      upsertPassengerOperation(addOperations, seatEntry.passengerId, addOn);
    } else {
      upsertPassengerOperation(updateOperations, seatEntry.passengerId, addOn);
    }
  });

  return { addOperations, updateOperations };
}

/**
 * Returns true when current seat selections differ from original booking seats.
 */
export function hasSeatSelectionChanges(originalPassengers, currentSeatArr) {
  const { addOperations, updateOperations } = buildSeatPnrOperationsFromDelta(
    originalPassengers,
    currentSeatArr
  );
  return addOperations.length > 0 || updateOperations.length > 0;
}

/**
 * Round-trip search passes { name, id, operatingCarrier } — enrich for @gigi/seat-map tabs.
 */
export function enrichFlightsForSeatMap(flightNames = []) {
  return flightNames.map((flight) => {
    if (flight.departureAirport && flight.arrivalAirport) {
      return flight;
    }
    const parts = (flight.name || '').split('-');
    return {
      ...flight,
      departureAirport: flight.departureAirport || parts[0] || '',
      arrivalAirport: flight.arrivalAirport || parts[1] || '',
      carrier: flight.carrier || flight.marketingCarrier,
      operatingCarrier: flight.operatingCarrier || flight.carrier,
    };
  });
}

/**
 * Merge per-flight API results into one results[] array (tab order = flightNames order).
 */
export function mergeSeatMapResults(prevData, normalizedChunk, flightId, flightNames) {
  const chunk = normalizedChunk?.results?.[0];
  if (!chunk) {
    return prevData;
  }

  const orderIndex = flightNames.findIndex(
    (f) => String(f.id) === String(flightId)
  );
  if (orderIndex < 0) {
    return prevData;
  }

  const results = Array.from({ length: flightNames.length }, (_, i) => {
    const existing = prevData?.results?.[i];
    return existing || null;
  });
  results[orderIndex] = chunk;

  return { results };
}

export function buildItineraryFlights(segments, cancelledCodes = ['HX', 'UC', 'UN', 'NO', 'WK']) {
  let iternaryFlights = [];
  if (!segments || !segments.length) {
    return iternaryFlights;
  }
  segments.forEach((obj) => {
    const key = obj && Object.keys(obj) && Object.keys(obj)[0];
    const flights = obj && obj[key] && obj[key].flights && obj[key].flights;
    const filtered =
      flights &&
      flights.filter(
        (flightData) => !cancelledCodes.includes(flightData?.statusDetail?.code)
      );
    if (filtered && filtered.length > 0) {
      iternaryFlights = [...iternaryFlights, ...filtered];
    }
  });
  return iternaryFlights;
}
